import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import moment from 'moment'
import { IconInfoCircle } from '@tabler/icons-react'

import { Progress } from '@/components/progress/Progress'
import { Button } from '@/components/buttons/Button'
import { Checkbox } from '@/components/forms/Checkbox'
import Select from '@/components/forms/Select'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import { Banner } from '@/components/banners/Banner'
import { Card } from '@/components/cards/Card'
import { Pill } from '@/components/pills/Pill'
import { toast } from '@/components/toasts/toast.helpers'
import { previewBulkScore, submitBulkScore } from '@/reducers/bulkScoring/bulkScoring.actions'
import { setData } from '@/reducers/bulkScoring/bulkScoring.redux'
import './BulkRescore.scss'

const MAX_DATE_RANGE_DAYS = 60
const MAX_DISPLAYED_ERRORS = 100
const SSE_THROTTLE_MS = 250

const JOB_STATUS = {
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  PENDING: 'pending',
  RUNNING: 'running',
}

const TERMINAL_STATUSES = [JOB_STATUS.COMPLETED, JOB_STATUS.FAILED, JOB_STATUS.CANCELLED]
const ACTIVE_STATUSES = [JOB_STATUS.PENDING, JOB_STATUS.RUNNING]

const BulkScoreTab = ({ selectedOrgId }) => {
  const dispatch = useDispatch()
  const { data: scorecardData, loading: scorecardLoading } = useSelector(
    (state) => state.scorecards
  )
  const { data: bulkData, loading: bulkLoading } = useSelector((state) => state.bulkScoring)

  const preview = bulkData.scorePreview
  const jobStatus = bulkData.scoreJobStatus

  const [selectedConfigId, setSelectedConfigId] = useState(null)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [dateInvalid, setDateInvalid] = useState(false)
  const [runEligibility, setRunEligibility] = useState(true)
  const [force, setForce] = useState(false)
  const [onlyExistingScorecards, setOnlyExistingScorecards] = useState(false)
  const abortControllerRef = useRef(null)
  const throttleTimerRef = useRef(null)
  const latestJobStatusRef = useRef(null)

  useEffect(() => {
    setSelectedConfigId(null)
    setOnlyExistingScorecards(false)
    dispatch(setData('scorePreview', null))
  }, [selectedOrgId])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current)
      }
    }
  }, [])

  const throttledSetJobStatus = (data) => {
    const isTerminal = TERMINAL_STATUSES.includes(data.status)
    latestJobStatusRef.current = data

    if (isTerminal) {
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current)
        throttleTimerRef.current = null
      }
      dispatch(setData('scoreJobStatus', data))
      return
    }

    if (!throttleTimerRef.current) {
      dispatch(setData('scoreJobStatus', data))
      throttleTimerRef.current = setTimeout(() => {
        throttleTimerRef.current = null
        if (latestJobStatusRef.current) {
          dispatch(setData('scoreJobStatus', latestJobStatusRef.current))
        }
      }, SSE_THROTTLE_MS)
    }
  }

  const scorecardOptions = useMemo(() => {
    return (scorecardData.scorecardConfigs || [])
      .filter((scorecard) => !scorecard?.deleted)
      .map((scorecard) => ({ value: scorecard.id, label: scorecard.name }))
  }, [scorecardData.scorecardConfigs])

  const handleScorecardSelect = (option) => {
    setSelectedConfigId(option?.value || null)
    dispatch(setData('scorePreview', null))
  }

  const handleDateChange = ({ selection }) => {
    const start = moment(selection.startDate).startOf('day')
    const end = moment(selection.endDate).startOf('day')
    const diff = end.diff(start, 'days')

    setDateInvalid(diff >= MAX_DATE_RANGE_DAYS)
    setStartDate(selection.startDate)
    setEndDate(selection.endDate)
    dispatch(setData('scorePreview', null))
  }

  const isJobActive = jobStatus && ACTIVE_STATUSES.includes(jobStatus.status)
  const canSubmit =
    selectedConfigId &&
    startDate &&
    endDate &&
    !dateInvalid &&
    !bulkLoading.scoreSubmitting &&
    !isJobActive

  const getRequestParams = () => ({
    scorecard_config_id: selectedConfigId,
    start_date: moment(startDate).startOf('day').toISOString(),
    end_date: moment(endDate).endOf('day').toISOString(),
    force,
    run_eligibility: runEligibility,
    only_existing_scorecards: onlyExistingScorecards,
  })

  const handlePreview = () => {
    if (!canSubmit) {
      return
    }
    dispatch(previewBulkScore(getRequestParams()))
  }

  const handleConfirmSubmit = () => {
    const controller = new AbortController()
    abortControllerRef.current = controller

    dispatch(
      submitBulkScore(getRequestParams(), {
        abortController: controller,
        onProgress: throttledSetJobStatus,
      })
    ).then(() => {
      abortControllerRef.current = null
    })
  }

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      dispatch(
        setData('scoreJobStatus', jobStatus ? { ...jobStatus, status: JOB_STATUS.CANCELLED } : null)
      )
      toast.info('Disconnected — job may still be running on the server')
    }
  }

  const totalItems = Number(jobStatus?.total_items) || 0
  const processedItems = Number(jobStatus?.processed) || 0
  const progressPercent = totalItems > 0 ? Math.round((processedItems / totalItems) * 100) : 0

  const getStatusPill = () => {
    const statusMap = {
      [JOB_STATUS.COMPLETED]: { success: true },
      [JOB_STATUS.FAILED]: { critical: true },
      [JOB_STATUS.CANCELLED]: { caution: true },
      [JOB_STATUS.RUNNING]: { informative: true },
      [JOB_STATUS.PENDING]: { informative: true },
    }
    const props = statusMap[jobStatus.status] || {}
    return (
      <Pill small emphasized {...props}>
        {jobStatus.status}
      </Pill>
    )
  }

  return (
    <>
      <Banner informative icon={<IconInfoCircle />} header="How bulk scoring works">
        <ul>
          <li>
            Only calls matching all scorecard config settings are eligible (users, tags, playbooks,
            duration, dispositions, VoIP campaigns, call type).
          </li>
          <li>
            Calls that already have a score for this config will be skipped unless &quot;Force
            re-score&quot; is enabled.
          </li>
          <li>
            Enable &quot;Only rescore calls that already have a scorecard&quot; with &quot;Force
            re-score&quot; to update existing scorecards to the latest config without scoring new
            calls.
          </li>
          <li>Use Preview to see how many calls will be scored before starting the job.</li>
        </ul>
      </Banner>

      <Card title="Configuration">
        <div className="bulk-operations-form">
          <div className="form-field">
            <label>Scorecard Config</label>
            <Select
              options={scorecardOptions}
              value={selectedConfigId}
              onChange={handleScorecardSelect}
              placeholder="Select a scorecard config..."
              loading={scorecardLoading.scorecards}
              isClearable
            />
          </div>

          <div className="form-field">
            <label>Date Range (max {MAX_DATE_RANGE_DAYS} days)</label>
            <DateRangePickerReusable
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateChange}
              error={dateInvalid}
            />
            {dateInvalid && (
              <Banner critical className="mt">
                Date range must be {MAX_DATE_RANGE_DAYS} days or less.
              </Banner>
            )}
          </div>

          <div className="form-field">
            <Checkbox
              label="Force re-score already scored calls"
              checked={force}
              onChange={(event, { checked }) => {
                setForce(checked)
                dispatch(setData('scorePreview', null))
              }}
            />
            <p className="form-field-hint">
              When enabled, existing scores for this config will be deleted and calls will be scored
              again.
            </p>
          </div>

          <div className="form-field">
            <Checkbox
              label="Only rescore calls that already have a scorecard"
              checked={onlyExistingScorecards}
              onChange={(event, { checked }) => {
                setOnlyExistingScorecards(checked)
                dispatch(setData('scorePreview', null))
              }}
            />
            <p className="form-field-hint">
              When enabled, only calls that already have a scorecard for this config will be
              processed. Unscored calls are skipped. Combine with &quot;Force re-score&quot; to
              update existing scorecards to the latest config.
            </p>
          </div>

          <div className="form-field">
            <Checkbox
              label="Run AI eligibility prompt before scoring each call"
              checked={runEligibility}
              onChange={(event, { checked }) => {
                setRunEligibility(checked)
                dispatch(setData('scorePreview', null))
              }}
            />
            <p className="form-field-hint">
              When enabled, each call is sent through the scorecard&apos;s eligibility prompt to
              verify it should be scored. This is slower but matches the standard scoring pipeline.
              Scorecard config settings (users, tags, duration, playbooks) are always enforced
              regardless of this option.
            </p>
          </div>

          <Button
            primary
            onClick={handlePreview}
            disabled={!canSubmit}
            loading={bulkLoading.scorePreview}
            data-testid="preview-bulk-score-btn"
          >
            Preview
          </Button>
        </div>
      </Card>

      {preview && (
        <Card title="Score Preview" className="bulk-operations-preview">
          <Banner informative icon={<IconInfoCircle />} header="Please confirm before proceeding">
            <div className="preview-details">
              <strong>Calls matching config settings:</strong>{' '}
              <span data-testid="preview-eligible-calls">{preview.total_eligible_calls}</span>
              <br />
              <strong>
                Already scored{force ? ' (will be re-scored)' : ' (will be skipped)'}:
              </strong>{' '}
              {preview.total_already_scored}
              <br />
              <strong>Calls to score:</strong> {preview.total_to_score}
              <br />
              <strong>Total LLM prompts:</strong>{' '}
              {preview.total_prompts != null ? preview.total_prompts.toLocaleString() : 'N/A'}
              <br />
              <strong>Estimated cost:</strong>{' '}
              <span data-testid="preview-estimated-cost">
                {preview.estimated_cost != null
                  ? `$${preview.estimated_cost.toFixed(2)}`
                  : 'N/A (no historical data)'}
              </span>
            </div>
            {!force && preview.total_to_score === 0 && preview.total_eligible_calls > 0 && (
              <p className="preview-warning">
                All eligible calls already have scores. Enable &quot;Force re-score&quot; to score
                them again.
              </p>
            )}
            {onlyExistingScorecards &&
              force &&
              preview.total_to_score === 0 &&
              preview.total_eligible_calls > 0 && (
                <p className="preview-warning">
                  No eligible calls have an existing scorecard for this config in this date range.
                </p>
              )}
            {preview.total_eligible_calls === 0 && (
              <p className="preview-warning">
                No eligible calls found for this config and date range.
              </p>
            )}
          </Banner>

          {preview.total_to_score > 0 && (
            <div className="preview-actions">
              <Button
                primary
                onClick={handleConfirmSubmit}
                disabled={bulkLoading.scoreSubmitting || isJobActive}
                loading={bulkLoading.scoreSubmitting}
                data-testid="confirm-bulk-score-btn"
              >
                {force ? 'Confirm & Re-score' : 'Confirm & Start Scoring'}
              </Button>
              <Button onClick={() => dispatch(setData('scorePreview', null))}>Cancel</Button>
            </div>
          )}
        </Card>
      )}

      {jobStatus && (
        <Card title="Job Status" className="bulk-operations-status">
          <div className="status-details">
            <span className="status-label">Job ID:</span>{' '}
            <span data-testid="job-id">{jobStatus.job_id}</span>
            <br />
            <span className="status-label">Status:</span> {getStatusPill()}
            <br />
            <span className="status-label">Progress:</span> {jobStatus.processed} /{' '}
            {jobStatus.total_items}
            <br />
            <span className="status-label">Succeeded:</span> {jobStatus.succeeded}
            <br />
            <span className="status-label">Skipped:</span> {jobStatus.skipped}
            <br />
            <span className="status-label">Failed:</span> {jobStatus.failed}
          </div>

          <Progress
            value={progressPercent}
            success={jobStatus.status === JOB_STATUS.COMPLETED && jobStatus.failed === 0}
            warning={jobStatus.status === JOB_STATUS.COMPLETED && jobStatus.failed > 0}
            critical={jobStatus.status === JOB_STATUS.FAILED}
          />

          {jobStatus.status === JOB_STATUS.RUNNING && (
            <Button onClick={handleCancel} className="mt">
              Cancel Job
            </Button>
          )}

          {!isEmpty(jobStatus.errors) && (
            <div className="errors-container">
              <h4>Errors ({jobStatus.errors.length})</h4>
              <div className="errors-list">
                {jobStatus.errors.slice(0, MAX_DISPLAYED_ERRORS).map((error, index) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${index}-${error.call_id || ''}-${error.error}`}
                    className="error-item"
                  >
                    {error.call_id && <span>Call {error.call_id}: </span>}
                    {error.error}
                  </div>
                ))}
                {jobStatus.errors.length > MAX_DISPLAYED_ERRORS && (
                  <div className="errors-overflow">
                    and {jobStatus.errors.length - MAX_DISPLAYED_ERRORS} more...
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      )}
    </>
  )
}

export default BulkScoreTab
